/**
 * Browsing and intent events for Google (GA4 and Google Ads), sent through the
 * tag that snippets/google-tag.liquid loads. Nothing here runs unless that
 * snippet rendered window.DuavaaGoogle, i.e. tracking is switched on.
 *
 *   view_item            product page load
 *   view_item_list       collection page load
 *   view_search_results  search page load
 *   add_to_cart          theme:product:add, which the cart dispatches after
 *                        every successful add (product form, quick add,
 *                        plating), with the cart's response
 *   begin_checkout       cart checkout button (India: Razorpay; elsewhere the
 *                        Quick Checkout button, which goes on to Stripe), the
 *                        custom checkout page, or Buy Now
 *
 * page_view is not sent from here: the tag's config sends it on load.
 * Purchases are not sent from here either: the planner reports them from the
 * order record, the only place the amount charged is known.
 *
 * ITEMS. Every item id is the Merchant Center offer id the planner's feeds
 * publish for this storefront, shopify_{LABEL}_{product}_{variant}, so Google
 * can match a viewed or added product to the catalogue the shopper sees.
 * Prices are what the shopper SAW: the INR price converted with the market's
 * rate and rounded up to a whole unit, exactly as snippets/market-price.liquid
 * displays it. India keeps the INR price as is.
 *
 * Shares nothing with the Meta scripts by design: no helper, no prefix logic.
 */
(function () {
  'use strict';

  var G = window.DuavaaGoogle;
  if (!G || window.__duavaaGoogleEventsLoaded) return;
  window.__duavaaGoogleEventsLoaded = true;
  if (typeof window.gtag !== 'function') return;

  var BRAND = 'Duavaa';

  /** Page data rendered by snippets/google-page-data.liquid, if readable. */
  function readPage() {
    try {
      var el = document.getElementById('duavaa-google-page');
      return (el && JSON.parse(el.textContent)) || {};
    } catch (e) {
      return {};
    }
  }
  var page = readPage();

  /**
   * Our destinations only. A tag some app adds to the page later must never
   * receive these events, or the live GA4 property would count them twice.
   */
  var sendTo = [];
  if (G.ga4Id) sendTo.push(G.ga4Id);
  if (G.adsId) sendTo.push(G.adsId);

  function log() {
    if (!G.debug || !window.console) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[google-events]');
    window.console.log.apply(window.console, args);
  }

  function send(name, params) {
    if (!sendTo.length) return;
    params.send_to = sendTo;
    log(name, params);
    try {
      window.gtag('event', name, params);
    } catch (e) {
      // Never let measurement break the page.
    }
  }

  // ── Prices and ids ────────────────────────────────────────────────────

  /** The price shown on the storefront, from Shopify's INR paise. */
  function shownPrice(paise) {
    var p = Number(paise) || 0;
    var rate = Number(G.rate) || 1;
    if (rate === 1) return Math.round(p) / 100;
    // Same integer steps as market-price.liquid, so the two cannot disagree
    // by a unit at a rounding boundary.
    var rateInt = Math.round(rate * 10000);
    var cents = Math.floor((p * rateInt) / 10000);
    return Math.floor((cents + 99) / 100);
  }

  function offerId(productId, variantId) {
    // Malaysia has a storefront but no feed yet; its ids use the market code
    // so they will match once a feed with that label exists.
    var label = G.feedLabel || String(G.market || '').toUpperCase();
    return 'shopify_' + label + '_' + productId + '_' + variantId;
  }

  /**
   * One GA4 / Ads item. `id` and google_business_vertical are what Ads
   * dynamic remarketing reads; GA4 reads item_id.
   */
  function item(src, index) {
    var id = offerId(src.product_id, src.variant_id);
    var out = {
      id: id,
      item_id: id,
      item_name: src.title || '',
      item_brand: BRAND,
      price: shownPrice(src.price),
      quantity: src.quantity || 1,
      google_business_vertical: 'retail'
    };
    if (src.variant_title && src.variant_title !== 'Default Title') out.item_variant = src.variant_title;
    if (src.type) out.item_category = src.type;
    if (typeof index === 'number') out.index = index;
    return out;
  }

  function valueOf(items) {
    var total = 0;
    for (var i = 0; i < items.length; i++) total += items[i].price * items[i].quantity;
    return Math.round(total * 100) / 100;
  }

  /** A cart line (cart.js or a cart/add response) in the shape item() takes. */
  function fromLine(line) {
    return {
      product_id: line.product_id,
      variant_id: line.variant_id,
      title: line.product_title || line.title,
      variant_title: line.variant_title,
      type: line.product_type,
      price: line.final_price != null ? line.final_price : line.price,
      quantity: line.quantity
    };
  }

  /** Paid add-ons (gold plating) ride on their own line, marked _Service. */
  function isServiceLine(line) {
    return !!(line && line.properties && line.properties._Service);
  }

  // ── Page events ───────────────────────────────────────────────────────

  function pageEvents() {
    var i;
    if (page.type === 'product' && page.items && page.items.length) {
      var viewed = [item(page.items[0])];
      send('view_item', { currency: G.currency, value: valueOf(viewed), items: viewed });
    } else if (page.type === 'collection' && page.items) {
      var listed = [];
      for (i = 0; i < page.items.length; i++) listed.push(item(page.items[i], i));
      send('view_item_list', {
        currency: G.currency,
        item_list_id: page.listId || '',
        item_list_name: page.listName || '',
        items: listed
      });
    } else if (page.type === 'search') {
      var found = [];
      for (i = 0; i < (page.items || []).length; i++) found.push(item(page.items[i], i));
      send('view_search_results', { currency: G.currency, search_term: page.searchTerm || '', items: found });
    }
  }

  // ── Cart snapshot: what was in the cart before an add ─────────────────

  var cartQty = {}; // variant id -> quantity, as last seen
  var lastCart = null;

  /** The cart as the server rendered it with this page: [[variant, qty], ...]. */
  function rememberRendered(pairs) {
    var items = [];
    for (var i = 0; i < (pairs || []).length; i++) {
      items.push({ variant_id: pairs[i][0], quantity: pairs[i][1] });
    }
    remember({ items: items });
  }

  function keyOf(line) {
    return String(line.variant_id);
  }

  function remember(cart) {
    lastCart = cart;
    cartQty = {};
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var k = keyOf(items[i]);
      cartQty[k] = (cartQty[k] || 0) + (items[i].quantity || 0);
    }
  }

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .catch(function () {
        return null;
      });
  }

  function cartHash(cart) {
    var items = (cart && cart.items) || [];
    var parts = [];
    for (var i = 0; i < items.length; i++) parts.push(items[i].variant_id + 'x' + items[i].quantity);
    return parts.sort().join('|');
  }

  // ── add_to_cart ───────────────────────────────────────────────────────

  var recentAdds = {};

  /** The lines an add event reports, without the plating add-on lines. */
  function addedLines(response) {
    if (!response) return [];
    if (response.items && response.items.length) {
      var real = [];
      for (var i = 0; i < response.items.length; i++) {
        if (!isServiceLine(response.items[i])) real.push(response.items[i]);
      }
      return real;
    }
    if (response.item) return isServiceLine(response.item) ? [] : [response.item];
    if (response.variant_id) return isServiceLine(response) ? [] : [response];
    return [];
  }

  function onAdd(event) {
    var response = event && event.detail && event.detail.response;
    var lines = addedLines(response);
    if (!lines.length) return;

    var items = [];
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      var k = keyOf(line);
      // cart/add.js answers with the line's NEW total quantity, so the amount
      // just added is that total minus what the cart held before.
      var before = cartQty[k] || 0;
      var added = (line.quantity || 1) - before;
      if (added < 1) added = 1;
      cartQty[k] = before + added;

      // The theme can announce one add twice (product and cart events).
      var guard = k + ':' + line.quantity;
      var now = Date.now();
      if (recentAdds[guard] && now - recentAdds[guard] < 2000) continue;
      recentAdds[guard] = now;

      var src = fromLine(line);
      src.quantity = added;
      items.push(item(src));
    }
    if (!items.length) return;
    send('add_to_cart', { currency: G.currency, value: valueOf(items), items: items });
  }

  // ── begin_checkout ────────────────────────────────────────────────────

  var CHECKOUT_KEY = 'duavaa_g_begin_checkout';
  var CHECKOUT_WINDOW_MS = 30 * 60 * 1000;

  /** Once per cart: the same cart re-entering checkout within 30 min is one start. */
  function alreadyStarted(hash) {
    try {
      var raw = window.sessionStorage.getItem(CHECKOUT_KEY);
      if (!raw) return false;
      var seen = JSON.parse(raw);
      return seen && seen.hash === hash && Date.now() - seen.at < CHECKOUT_WINDOW_MS;
    } catch (e) {
      return false;
    }
  }

  function markStarted(hash) {
    try {
      window.sessionStorage.setItem(CHECKOUT_KEY, JSON.stringify({ hash: hash, at: Date.now() }));
    } catch (e) {
      // Private mode: fall back to the in-page guard below.
    }
  }

  var startedHashes = {};

  function beginCheckout(cart, via) {
    if (!cart || !cart.items || !cart.items.length) return;
    var hash = cartHash(cart);
    if (startedHashes[hash] || alreadyStarted(hash)) return;
    startedHashes[hash] = true;
    markStarted(hash);

    var items = [];
    for (var i = 0; i < cart.items.length; i++) {
      if (!isServiceLine(cart.items[i])) items.push(item(fromLine(cart.items[i])));
    }
    if (!items.length) return;
    // The cart total includes plating add-ons, so value is the cart's own total.
    var value = shownPriceTotal(cart);
    log('begin_checkout via', via);
    send('begin_checkout', { currency: G.currency, value: value, items: items });
  }

  /** The cart total as the shopper sees it: every line converted and summed. */
  function shownPriceTotal(cart) {
    var total = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var line = cart.items[i];
      var unit = line.final_price != null ? line.final_price : line.price;
      total += shownPrice(unit) * (line.quantity || 1);
    }
    return Math.round(total * 100) / 100;
  }

  function checkoutFromCart(via) {
    fetchCart().then(function (cart) {
      beginCheckout(cart, via);
    });
  }

  /**
   * Buy Now adds the product to the cart and checks out the whole cart, so
   * wait until the cart has changed, then report it. Gives up after ~4 s and
   * reports whatever the cart holds.
   */
  function checkoutAfterBuyNow() {
    var before = lastCart ? cartHash(lastCart) : null;
    var tries = 0;
    (function poll() {
      tries++;
      fetchCart().then(function (cart) {
        var changed = cart && cartHash(cart) !== before;
        if (changed || tries >= 8) {
          if (cart) remember(cart);
          beginCheckout(cart, 'buy_now');
          return;
        }
        setTimeout(poll, 500);
      });
    })();
  }

  // India's button submits the cart form (Razorpay takes over); every other
  // market's is the Quick Checkout button, which leads to Stripe and never
  // passes through the custom checkout page.
  var CART_CHECKOUT =
    'button[name="checkout"], input[name="checkout"], .cart__checkout, ' +
    '[data-tally-checkout-button], .cart__checkout--tally';
  var BUY_NOW = '[data-buy-now-button], [data-buy-now-button-india]';

  function onClick(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    if (target.closest(BUY_NOW)) {
      checkoutAfterBuyNow();
    } else if (target.closest(CART_CHECKOUT)) {
      checkoutFromCart('cart');
    }
  }

  // ── Wiring ────────────────────────────────────────────────────────────

  // Capture phase on window, so a checkout script that stops the click
  // (Razorpay's does) cannot hide it from us. Listening never interferes.
  window.addEventListener('click', onClick, true);
  // theme:cart:add is only the REQUEST to add and carries no result; the
  // cart answers it with theme:product:add, so that one event covers all adds.
  document.addEventListener('theme:product:add', onAdd);

  function start() {
    rememberRendered(page.cart);
    pageEvents();
    // The international cart checkout lands on this page; Buy Now does not.
    // The one place the full cart is fetched on load.
    if (page.type === 'checkout') {
      fetchCart().then(function (cart) {
        if (cart) remember(cart);
        beginCheckout(cart, 'cart');
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
